import { useMemo, useState } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import BarChart from '@shared/charts/BarChart';
import { UMR } from '../../urbanmap/_components';
import type { PlanGeoData } from '../proposition-c/types';
import { buildingsByIds, YEARS } from './engine';
import { routeOf } from './construction';
import { segmentKm } from './sectionEngine';
import { setsOf } from './model';
import type { Plan, Project } from './types';

export function PlanCharts({ project, plan, geo, kind }: { project: Project; plan: Plan; geo?: PlanGeoData; kind: 'buildings' | 'phasing' }) {
  const [cumulative, setCumulative] = useState(false);
  const charts = useMemo(() => {
    const buildings = buildingsByIds(plan.buildingIds ?? []);
    const coef = (setsOf(project, plan)?.demande.consoCoef ?? 100) / 100 * (plan.energy === 'froid' ? 0.35 : 1);
    const group = (key: 'usage' | 'heatingEnergy', value: 'heat' | 'area') => {
      const groups = new Map<string, number>();
      buildings.forEach((b) => groups.set(b[key], (groups.get(b[key]) ?? 0) + (value === 'heat' ? b.heat * coef : b.area ?? 0)));
      return { labels: [...groups.keys()], values: [...groups.values()] };
    };
    if (kind === 'buildings') return [
      { title: 'Consommation par usage', unit: 'MWh/an', ...group('usage', 'heat') },
      { title: 'Surface par usage', unit: 'm²', ...group('usage', 'area') },
      { title: 'Ancienne énergie de chauffage', unit: 'MWh/an', ...group('heatingEnergy', 'heat') },
    ];
    const route = routeOf(plan, geo?.pipes.features.length ?? 0);
    return [
      { title: 'Consommation raccordée par an', unit: 'MWh/an', values: YEARS.map((year) =>
        buildings.filter((b) => (plan.buildingPhases?.[b.id] ?? YEARS[0]) === year).reduce((sum, b) => sum + b.heat * coef, 0)) },
      { title: 'Bâtiments raccordés par an', unit: 'bâtiments', values: YEARS.map((year) =>
        (plan.buildingIds ?? []).filter((id) => (plan.buildingPhases?.[id] ?? YEARS[0]) === year).length) },
      { title: 'Réseau posé par an', unit: 'km', values: YEARS.map((year) =>
        route.indexes.filter((index) => route.phases[index] === year).reduce((sum, index) => sum + segmentKm(geo, index), 0)) },
    ].map((chart) => ({ ...chart, labels: YEARS }));
  }, [project, plan, geo, kind]);
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
    <NJText as="h3" scale="lg">{kind === 'buildings' ? 'Synthèse des bâtiments' : 'Synthèse annuelle du phasage'}</NJText>
    {kind === 'phasing' && <div style={{ display: 'flex', gap: 8 }}>
      <NJButton label="Annuel" scale="sm" variant={!cumulative ? 'primary' : 'secondary'} onClick={() => setCumulative(false)} />
      <NJButton label="Cumulé" scale="sm" variant={cumulative ? 'primary' : 'secondary'} onClick={() => setCumulative(true)} />
    </div>}
    {!plan.buildingIds?.length && <NJText role="status">Aucun bâtiment ; les séries sont vides.</NJText>}
    {charts.map((chart) => {
      let total = 0;
      const values = chart.values.map((value) => cumulative && kind === 'phasing' ? (total += value) : value);
      return <section key={chart.title} style={{ border: `1px solid ${UMR.border}`, padding: 16, minWidth: 0 }}>
        <NJText as="h4" scale="md">{chart.title} · {chart.unit}{cumulative && kind === 'phasing' ? ' · cumulé' : ''}</NJText>
        <div style={{ height: 260 }}>
          <BarChart data={{ labels: chart.labels, datasets: [{ label: chart.unit, data: values, backgroundColor: '#00726d' }] }}
            options={{ maintainAspectRatio: false, plugins: { legend: { display: true } }, scales: { y: { beginAtZero: true, title: { display: true, text: chart.unit } } } }} />
        </div>
      </section>;
    })}
    <NJText scale="xs">Source : copie Gentilly, estimation chaleur modulée par Demande (froid ×0,35 simulé).
      Surface manquante exclue des sommes : {buildingsByIds(plan.buildingIds ?? []).filter((b) => b.area == null).length} bâtiment(s).
      Énergie inconnue : « Non renseigné ». Dates absentes : 2027, convention de démonstration ; aucune puissance calculée.</NJText>
  </div>;
}
